// A research run's progress in the reader's terms, for the run status panel
// (components/RunStatus.jsx): which phase it is in, how many sources it has read, the evidence
// cards it has written, the questions its research threads are exploring, the latest thing it
// found, and the triggers it has settled on. Everything here is read from the typed stream
// (`update` frames) and the narration a Mirabilis lane sends, deep or quick; none of it names a
// tool, a model or an agent. Pure.
import type { AnyProgressUpdate, UnknownUpdate } from "@sooth-labs/idl";
import { laneTier } from "./engines.js";
import { readerCardText } from "./evidenceCards.js";
import { fmtPct, sideChanceText } from "./formatPct.js";
import { EMPTY_RESEARCH, STATUS } from "./model.js";
import type {
  EvidenceStub,
  NarrationBeat,
  ResearchProgress,
  ResearchThread,
  Run,
} from "./model.js";
import type { Headline } from "./progressView.js";
import { siteNameOf } from "../report/sourceLabel.js";

const EVIDENCE_TOOL = "write_evidence_card";
// `spawn_researcher` is what the deep lane sends; `spawn` is what the test scenarios send.
const THREAD_TOOLS = new Set(["spawn_researcher", "spawn"]);
const MAX_THREADS = 40;
const MAX_CARDS = 80;

/**
 * Whether a run streams its research as it goes: a Mirabilis lane, deep or quick (the same test
 * the report uses, engines.js madeWithMirabilis). Other lanes send only coarse stage updates.
 */
export function streamsResearch(run: Pick<Run, "backend"> | null | undefined): boolean {
  return !!run?.backend && /mirabilis/i.test(run.backend);
}

const record = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" ? (v as Record<string, unknown>) : {};

/** `research` with one more update folded in; the same object when the update changes nothing. */
export function foldResearch(
  research: ResearchProgress,
  update: AnyProgressUpdate | UnknownUpdate,
): ResearchProgress {
  const u = update as unknown as Record<string, unknown>;
  if (u.kind === "tool_call_started" && u.tool === EVIDENCE_TOOL) {
    const detail = record(u.detail);
    const count = Number(detail.card_count);
    const n = Number.isFinite(count) && count > 0 ? count : research.sources + 1;
    const at = typeof u.occurred_at === "string" ? u.occurred_at : research.lastSourceAt;
    const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
    const card: EvidenceStub = {
      n,
      title: text(detail.title) || text(u.title),
      source: text(detail.source),
      url: text(detail.url),
      angle: text(detail.angle),
    };
    const known = (research.cards ?? []).some((c) => c.n === n);
    return {
      ...research,
      sources: Math.max(research.sources, n),
      lastSourceAt: at,
      cards: known ? research.cards : [...(research.cards ?? []), card].slice(-MAX_CARDS),
    };
  }
  if (u.kind === "tool_call_started" && THREAD_TOOLS.has(String(u.tool))) {
    const callId = String(u.call_id ?? "");
    if (!callId || research.threads.some((t) => t.callId === callId)) return research;
    const detail = record(u.detail);
    const mission =
      typeof detail.mission === "string" && detail.mission.trim()
        ? detail.mission.trim()
        : typeof u.title === "string"
          ? u.title.trim()
          : "";
    if (!mission) return research;
    const depth = Number(detail.depth);
    const thread: ResearchThread = {
      callId,
      mission,
      depth: Number.isFinite(depth) ? depth : null,
      done: false,
    };
    return { ...research, threads: [...research.threads, thread].slice(-MAX_THREADS) };
  }
  if (u.kind === "tool_call_finished") {
    const callId = String(u.call_id ?? "");
    const at = research.threads.findIndex((t) => t.callId === callId && !t.done);
    if (at < 0) return research;
    const threads = research.threads.slice();
    threads[at] = { ...threads[at]!, done: true };
    return { ...research, threads };
  }
  return research;
}

export const researchOf = (run: Pick<Run, "progressResearch">): ResearchProgress =>
  run.progressResearch ?? EMPTY_RESEARCH;

// ── Phases ───────────────────────────────────────────────────────────────

export const RESEARCH_PHASES = Object.freeze([
  { key: "question", label: "Checking your question" },
  { key: "first-read", label: "Making a first read" },
  { key: "evidence", label: "Gathering evidence" },
  { key: "weighing", label: "Weighing the evidence" },
  { key: "writing", label: "Writing the forecast" },
]);

/**
 * How long research must stay quiet, with no thread still open, before a run that never opened
 * a thread counts as weighing what it found. A run that did open threads weighs once they have
 * all reported back.
 */
export const WEIGHING_QUIET_MS = 2 * 60 * 1000;

export type PhaseState = "done" | "current" | "todo";

export interface DeepPhase {
  key: string;
  label: string;
  state: PhaseState;
  detail: string | null;
}

const headlineText = (headline: Headline | null): string | null => {
  if (!headline) return null;
  if (headline.kind === "probability") return sideChanceText(headline.value);
  let best = -1;
  let label: string | undefined;
  headline.mass.forEach((m, i) => {
    if (m > best) {
      best = m;
      label = headline.labels[i];
    }
  });
  return label ? `${label} ${fmtPct(best)}%` : null;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Whether the run has reached its write-up: an answer past the first read, its report, or its triggers. */
function writing(run: Run): boolean {
  if (run.progressTailReport) return true;
  if ((run.progressDoc?.futureTriggers?.length ?? 0) > 0) return true;
  if (run.progressDoc?.outcome != null) return true;
  const seedRevisions = run.progressSeedOutcome?.headline ? 1 : 0;
  return run.progressForecastRevisions > seedRevisions;
}

/**
 * Whether the run makes a first read: a deep run always does (it seeds within its first
 * seconds); another lane does once its seed call starts.
 */
const makesFirstRead = (run: Run): boolean =>
  run.progressSeedStarted || laneTier(run.backend || "") === "deep";

/** Index into RESEARCH_PHASES of the phase the run is in. */
export function researchPhaseIndex(run: Run, nowMs: number): number {
  if (run.status === STATUS.PREPROCESSING) return 0;
  if (writing(run)) return 4;
  const research = researchOf(run);
  const open = research.threads.some((t) => !t.done);
  if (!open && research.threads.length > 0) return 3;
  if (!open && research.sources > 0) {
    const last = Date.parse(research.lastSourceAt ?? "");
    if (Number.isFinite(last) && nowMs - last >= WEIGHING_QUIET_MS) return 3;
  }
  if (run.progressSeedOutcome || research.sources > 0 || research.threads.length > 0) return 2;
  return makesFirstRead(run) ? 1 : 2;
}

/**
 * The phase list, each with its state and a short detail where there is one to give. A run
 * that makes no first read has no row for it.
 */
export function researchPhases(run: Run, nowMs: number): DeepPhase[] {
  const at = researchPhaseIndex(run, nowMs);
  const research = researchOf(run);
  const triggers = run.progressDoc?.futureTriggers?.length ?? 0;
  const firstRead = makesFirstRead(run);
  return RESEARCH_PHASES.flatMap((phase, i) => {
    if (phase.key === "first-read" && !firstRead) return [];
    let detail: string | null = null;
    if (phase.key === "first-read" && run.progressSeedOutcome?.ok)
      detail = headlineText(run.progressSeedOutcome.headline);
    if (phase.key === "evidence" && research.sources > 0)
      detail = plural(research.sources, "source", "sources");
    if (phase.key === "writing" && triggers > 0) detail = plural(triggers, "trigger", "triggers");
    return [
      {
        key: phase.key,
        label: phase.label,
        state: i < at ? "done" : i === at ? "current" : "todo",
        detail,
      },
    ];
  });
}

/** Deep runs usually finish inside this; past it the panel says so. */
export const DEEP_USUAL_MS = 60 * 60 * 1000;
/** Quick research runs usually finish inside this. */
export const QUICK_USUAL_MS = 15 * 60 * 1000;

/** The duration line under the panel, for the run's tier. */
export function researchDurationCopy(tier: string | null, elapsedMs: number): string {
  const leave = "You can leave this page; it keeps running and appears in Forecasts.";
  if (tier === "deep") {
    if (elapsedMs >= DEEP_USUAL_MS)
      return `This one is taking longer than usual; a deep forecast can run for up to about 100 minutes. ${leave}`;
    return `Deep forecasts usually take 30 to 60 minutes. ${leave}`;
  }
  if (elapsedMs >= QUICK_USUAL_MS) return `This one is taking longer than usual. ${leave}`;
  return `This usually takes a few minutes. ${leave}`;
}

// ── The latest finding ───────────────────────────────────────────────────

// Text that talks about the forecaster rather than the question: who is working ("the research
// team", "the lead researcher", "a sub-agent"), what runs it ("the LLM", "an AI model"), or what it
// produced ("the final forecast", "two evidence cards"). Bare words ("team", "agents", "models")
// only count as the subject of the sentence, so a finding about a team, free agents or car
// models still reads.
const FORECASTER_TERMS =
  /\b(sub-?researchers?|(?:lead|root)\s+researchers?|research\s+team|sub-?agents?|(?:an?|ai|research)\s+agents?|llms?|large\s+language\s+models?|(?:ai|language|forecasting)\s+models?|spawn(?:ed|ing)?|sayer|forecaster|(?:final|initial|first|updated)\s+forecast|evidence\s+cards?|pipeline|harness|tool\s+calls?)\b/i;
const FORECASTER_SUBJECT =
  /^(?:the\s+)?(?:team|agents?|model|system|assistant|ai|tools?)\b|^the\s+researchers?\b/i;

/** Whether text talks about the forecaster itself, which the panel never shows. */
export function mentionsForecaster(text: string): boolean {
  const t = String(text || "").trim();
  return FORECASTER_TERMS.test(t) || FORECASTER_SUBJECT.test(t);
}

// A beat that only says what was searched ("The lead researcher searched the web for
// information regarding Bose ownership…"): the topic is worth showing, the searching is not.
const SEARCHED =
  /\b(?:searched|looked|researched|retrieved|gathered|collected|pulled|checked|investigated|reviewed|read)\b.*?\b(?:for|regarding|about|on|into)\s+(?:(?:information|details|data|news|sources|articles)\s+)?(?:(?:regarding|about|on|into|from\s+[^,]*?\s+(?:regarding|about|on))\s+)?(.+)$/i;
// What follows the topic in such a beat: the next step of the work, or why it was done.
const AFTER_TOPIC =
  /\s*(?:,\s*)?\b(?:and\s+(?:then\s+)?(?:reviewed|wrote|read|checked|searched|gathered|compiled|looked|collected|retrieved|found)\b|to\s+(?:continue|gather|find|check|build|inform|support|confirm|understand)\b|in\s+order\s+to\b).*$/i;

/**
 * A narration beat as the panel shows it: a finding as written, a search reduced to its topic
 * ("Looking into Bose ownership, …"), and null for anything about the forecaster itself.
 */
export function readerBeat(text: string, phase: string | null = null): string | null {
  const t = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.;:]+$/, "");
  if (t.length < 12 || phase === "code") return null;
  const searched = SEARCHED.exec(t);
  if (searched) {
    const topic = searched[1]!
      .replace(AFTER_TOPIC, "")
      .replace(/[.,;:]+$/, "")
      .trim();
    if (topic.length < 4 || mentionsForecaster(topic)) return null;
    return `Looking into ${topic}`;
  }
  return mentionsForecaster(t) ? null : t;
}

/** The newest narration beat worth showing a reader, as readerBeat shows it, or null. */
export function latestFinding(narration: readonly NarrationBeat[]): string | null {
  for (let i = narration.length - 1; i >= 0; i--) {
    const beat = narration[i]!;
    const shown = readerBeat(typeof beat.text === "string" ? beat.text : "", beat.phase);
    if (shown) return shown;
  }
  return null;
}

// ── Questions being explored ─────────────────────────────────────────────

/** The threads worth listing: the run's own, not threads its threads opened. */
export function listedThreads(research: ResearchProgress): readonly ResearchThread[] {
  return research.threads.filter((t) => t.depth == null || t.depth <= 1);
}

const JARGON: [RegExp, string][] = [
  [/^\s*attack the no case (?:for|on)\s+/i, "What could lead to yes on "],
  [/^\s*attack the yes case (?:for|on)\s+/i, "What could lead to no on "],
  [/^\s*steelman (?:the )?yes(?: case)? (?:for|on)\s+/i, "The strongest case for yes on "],
  [/^\s*steelman (?:the )?no(?: case)? (?:for|on)\s+/i, "The strongest case for no on "],
  [/^\s*historical base rate:\s*/i, ""],
];
const QUESTION_WORD = /^(how|what|whether|which|when|will|is|are|does|do|can|could|why)\b/i;
const MAX_QUESTION_CHARS = 110;

/**
 * A research brief made readable without a model: jargon reworded, parentheticals and
 * instructions after the first clause dropped, shouting lowered, and the result cut at a word.
 */
export function plainQuestion(mission: string): string {
  let s = String(mission || "")
    .replace(/\s+/g, " ")
    .trim();
  for (const [re, to] of JARGON) s = s.replace(re, to);
  s = s.replace(/\s*\([^)]*\)/g, "");
  const colon = s.indexOf(": ");
  if (colon > 0) {
    const before = s.slice(0, colon);
    const after = s.slice(colon + 2);
    s = QUESTION_WORD.test(after) && !QUESTION_WORD.test(before) ? after : before;
  }
  s = s.split(/\s+(?:—|–|-)\s+|;\s+/)[0]!;
  // Shouted words in a brief ("CUT", "HIKE") read as yelling; acronyms and tickers ("EUR/USD") stay.
  s = s.replace(/(?<![/\w])([A-Z]{3,})(?![/\w])/g, (w) =>
    /^(FOMC|CPI|PCE|GDP|NATO|OPEC|NASA|ECB|FDA|SEC|IMF|WHO|BOJ)$/.test(w) ? w : w.toLowerCase(),
  );
  s = s.trim().replace(/[.:,;]+$/, "");
  if (s.length > MAX_QUESTION_CHARS) {
    const cut = s.slice(0, MAX_QUESTION_CHARS);
    s = `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 40))}…`;
  } else if (QUESTION_WORD.test(s) && !s.endsWith("?")) {
    s = `${s}?`;
  }
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

/**
 * The cache key for one brief's plain question: the forecast's question and the brief together,
 * since the rewording reads both (questionsPrompt). Lossless, so no two pairs share a key.
 */
export const briefKey = (question: string, mission: string): string =>
  JSON.stringify([question, mission]);

/** A batch of briefs as one string (a stable effect dependency), and back; lossless. */
export const encodeBriefs = (missions: readonly string[]): string => JSON.stringify(missions);
export const decodeBriefs = (encoded: string): string[] => {
  try {
    const v: unknown = JSON.parse(encoded);
    return Array.isArray(v) ? v.filter((m): m is string => typeof m === "string") : [];
  } catch {
    return [];
  }
};

/** The prompt that asks Sayer's fast model to reword research briefs as plain questions; its reply is JSON. */
export function questionsPrompt(question: string, missions: readonly string[]): string {
  return [
    "A forecaster is researching this question:",
    JSON.stringify(String(question || "").slice(0, 300)),
    "Below are briefs it gave its research assistants. Rewrite each one as a short, plain-English",
    "question that a reader with no background would understand: at most 12 words, no jargon",
    '(not "steelman", "base rate", "YES/NO case", "angle"), no instructions, no ticker symbols.',
    "Ask about the world, never about the research: no researchers, team, agents, AI or models.",
    "Say what is being checked, as a question.",
    "Briefs:",
    ...missions.map((m, i) => `${i + 1}. ${JSON.stringify(m.slice(0, 400))}`),
    'Reply with JSON only, no prose: {"1": "<question>", "2": "<question>", ...}',
  ].join("\n");
}

/** The model's reply as one question per brief (null where it gave none), or null when it is not the JSON asked for. */
export function parseQuestions(reply: string, n: number): (string | null)[] | null {
  const m = /\{[\s\S]*\}/.exec(String(reply || ""));
  if (!m) return null;
  let o: unknown;
  try {
    o = JSON.parse(m[0]);
  } catch {
    return null;
  }
  if (!o || typeof o !== "object" || Array.isArray(o)) return null;
  const out: (string | null)[] = [];
  for (let i = 1; i <= n; i++) {
    const v = (o as Record<string, unknown>)[String(i)];
    const text = typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";
    out.push(text && text.length <= 140 ? text : null);
  }
  return out;
}

// ── Triggers ─────────────────────────────────────────────────────────────

export interface FoundTrigger {
  id: string;
  title: string;
  /** The first day of its window (YYYY-MM-DD), or null. */
  date: string | null;
  /** Which way it would move a yes/no forecast if it happens, or null when that is not known. */
  direction: "up" | "down" | null;
}

/** The triggers the run has settled on, soonest first. */
export function triggersFound(run: Run): FoundTrigger[] {
  const doc = run.progressDoc;
  if (!doc) return [];
  const p0 = doc.forecast?.form === "bernoulli" ? doc.forecast.probability : null;
  const out: FoundTrigger[] = [];
  for (const t of doc.futureTriggers ?? []) {
    if (typeof t?.title !== "string" || !t.title.trim()) continue;
    const starts = Array.isArray(t.when) ? t.when.map((w) => w?.starts).filter(Boolean) : [];
    const date = starts.length ? (starts.slice().sort()[0] as string) : null;
    let direction: FoundTrigger["direction"] = null;
    const ifOccurs = t.score?.if_occurs;
    if (p0 != null && ifOccurs?.form === "bernoulli" && ifOccurs.probability !== p0)
      direction = ifOccurs.probability > p0 ? "up" : "down";
    out.push({ id: t.id, title: t.title.trim(), date, direction });
  }
  return out.sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999"));
}

// ── Evidence cards ───────────────────────────────────────────────────────

export interface EvidenceTile {
  n: number;
  /** "E3": the label the finished report gives the same card. */
  label: string;
  title: string;
  /** Where it came from, for a reader: "bls.gov", "News archive", or "". */
  source: string;
  url: string;
  /** The line of research it came from ("Base rate"), or null for the lead's own. */
  angle: string | null;
}

const SAFE_URL = /^https?:\/\//i;

/** A source host or kind as a reader reads it. */
function sourceName(source: string, url: string): string {
  if (source === "article") return "News archive";
  let host = source;
  if (!host && SAFE_URL.test(url)) {
    try {
      host = new URL(url).hostname;
    } catch {
      host = "";
    }
  }
  return siteNameOf(host);
}

/** "base_rate" → "Base rate"; the lead researcher's own cards ("tree") carry none. */
function angleName(angle: string): string | null {
  if (!angle || angle === "tree") return null;
  const words = angle.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
}

// A mid-run card title carries the research's own labels ("Challenger: dovish-CPI YES path —",
// "Root synthesis:") and tool names ("query_series"); the report's cleaner (readerCardText) does
// not know these, so they are cut here.
const STUB_LABEL =
  /^\s*(?:challenger|root synthesis|root researcher|lead researcher|synthesis)\s*:\s*/i;
const STUB_TOOLS = /\s*(?:\+\s*)?\b(?:query_series|run_python|signals_query)\b/gi;

/** A mid-run card title as a reader reads it. */
export function stubTitle(title: string): string {
  const t = readerCardText(title, { title: true })
    .replace(STUB_LABEL, "")
    .replace(/\b(YES|NO) path\b/g, (_m, side: string) => `path to ${side.toLowerCase()}`)
    .replace(STUB_TOOLS, "")
    .replace(/\bproprietary\s+/gi, "")
    .replace(/\s+,/g, ",")
    .replace(/\s+/g, " ")
    .trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}

/** The run's evidence cards so far, as the panel shows them, oldest first. */
export function evidenceTiles(research: ResearchProgress): EvidenceTile[] {
  return (research.cards ?? []).map((c) => {
    const source = sourceName(c.source, c.url);
    const title = stubTitle(c.title) || c.title;
    return {
      n: c.n,
      label: `E${c.n}`,
      title:
        title && !mentionsForecaster(title)
          ? title
          : source
            ? `Evidence from ${source}`
            : `Evidence ${c.n}`,
      source,
      url: SAFE_URL.test(c.url) ? c.url : "",
      angle: angleName(c.angle),
    };
  });
}
